import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import UniformSkeleton from '../../../../uniform/components/skeleton/src/skeleton';
import { METER_CONTENT } from '../content/meter-content';
import { useResetAll, useSelector } from '../context/hooks';
import {
	getCurrencyIsoCode,
	getNumberFormatLocale,
	isCcpDataPending,
	selectHasOverage,
	selectIncludedAllowance,
	selectMeter,
	selectSelectedProducts,
	selectTotalMonthlyCost,
	selectTotalOverageCost,
	selectTotalOverageUnits,
} from '../context/selectors';
import {
	useUbpStep5BackClickedEvent,
	useUbpStep5FeedbackClickedEvent,
	useUbpStep5ScreenViewedEvent,
	useUbpStep5ShareClickedEvent,
} from '../ubp-calculator-analytics';

import { EstimateDisclaimer } from './estimate-disclaimer';
import { FeedbackButtons, type FeedbackVote } from './feedback-buttons';
import { HowYourTeamsMightUse } from './how-your-teams-might-use';
import { OrgNeedsResultsCard } from './org-needs-results-card';
import { ProductCardForKey } from './product-cards';
import { messages as restartMessages } from './restart-button.messages';
import { ResultsCard } from './results-card';
import { ShareButton } from './share-button';
import { Stepper } from './stepper';
import {
	buildOverageRateValues,
	formatNumber,
	formatNumberRange,
	formatPrice,
	formatPriceRange,
} from './strings';
import { messages } from './summary-step.messages';

import type { MessageDescriptor } from 'react-intl';

// ─────────────────────────────────────────────────────────────────────────
// Summary step layout (per Figma node 1103:32406):
//
//   Header (80px above H3):
//     H3: "Review usage estimate and compare"
//     Body Large: dynamic subtitle
//
//   ┌───────────────────────────────────┬────────────────────────────┐
//   │  ResultsCard: "Your org needs"    │                            │
//   │                                   │                            │
//   │  ResultsCard: "What's covered"    │  [ Selected product /      │
//   │                                   │    collection cards ]      │
//   │  HowYourTeamsMightUse             │                            │
//   │  (value cards WITHOUT icons)      │                            │
//   └───────────────────────────────────┴────────────────────────────┘
//
//   FeedbackButtons (centered)
//   [Back]  [Copy URL to share]
//   Non-binding-estimate disclaimer
//
// Layout parity with the Estimate step (same header treatment, same
// feedback wrapper, same bottom action row + disclaimer) so navigating
// between the two steps feels consistent.
// ─────────────────────────────────────────────────────────────────────────

export const SummaryStep = () => {
	const intl = useIntl();
	const meter = useSelector(selectMeter);
	const resetAll = useResetAll();
	const pending = useSelector(isCcpDataPending);
	const [vote, setVote] = useState<FeedbackVote>(null);

	const fireStep5Viewed = useUbpStep5ScreenViewedEvent();
	// GWP-12674 — analytics event reused for the new Restart button.
	// The old "Back" gesture becomes "Restart" semantically; the event
	// hook name is intentionally kept for funnel continuity.
	const fireStep5Back = useUbpStep5BackClickedEvent();
	const fireStep5Share = useUbpStep5ShareClickedEvent();
	const fireStep5Feedback = useUbpStep5FeedbackClickedEvent();

	useEffect(() => {
		fireStep5Viewed();
		// Intentionally run once on mount only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Stepper />
			{/*
			 * Summary step header per Figma node 1103:32406:
			 *   H3 (Charlie Display Bold 32/38) "Review usage estimate and compare"
			 *   Body Large (Charlie Text Regular 20/28) subtitle
			 *
			 * 80px padding above the H3 (40 base + 40 added per design
			 * refinement) and 40px below the subtitle.
			 *
			 * Wrapped in TypographyStyles so raw `h3` inherits the WAC
			 * design-system Charlie font stack (same pattern used on the
			 * Estimate step).
			 */}
			{/*
			 * Meter-aware header — title and subtitle both sourced from
			 * the atomic content registry (`summaryStep` field). Message
			 * IDs are scoped per meter so translators can localize the
			 * entire byline (which embeds the meter name mid-sentence)
			 * atomically per locale.
			 */}
			<TypographyStyles>
				<div css={pageHeaderStyle}>
					<h3 css={pageTitleStyle}>{intl.formatMessage(METER_CONTENT[meter].summaryStep.title)}</h3>
					<p css={pageSubtitleStyle}>
						{intl.formatMessage(METER_CONTENT[meter].summaryStep.subtitle)}
					</p>
				</div>
			</TypographyStyles>
			{/*
			 * While CCP pricing data is still loading, show skeletons below the
			 * header in place of the summary content. Two equal-width columns:
			 * left has three 300px bones, right is a single 600px bone.
			 */}
			{pending ? (
				<SummarySkeleton />
			) : (
				<>
					<div css={twoColumnRowStyle}>
						<div css={leftColumnStyle}>
							<SummaryResultsCards />
							<HowYourTeamsMightUse />
						</div>
						<div css={rightColumnStyle}>
							<SelectedProductCards />
						</div>
					</div>

					{/*
					 * Feedback buttons — sit between the content and the Back/Share
					 * row per Figma node 1103:32406.
					 */}
					<div css={feedbackWrapperStyle}>
						<FeedbackButtons
							vote={vote}
							onVote={(v) => {
								setVote(v);
								// Only fire the analytics event when the user actually
								// casts a vote. `v` can be null when they toggle their
								// current vote off, in which case there's nothing to
								// report.
								if (v !== null) {
									fireStep5Feedback(v);
								}
							}}
						/>
					</div>
					{/*
					 * Bottom action row per Figma (updated per GWP-12674 — Back
					 * replaced with Restart):
					 *   [Restart]  [Copy URL to share]
					 *
					 * Both buttons sit side-by-side, vertically centered.
					 *
					 * RESTART: resets the entire calculator flow back to Step 1
					 * (Usage type) with a completely blank state. See
					 * `useResetAll` / `resetAll` in the calculator context.
					 * Uses WAC design-library `<Button variant="secondary">` since
					 * this is an in-page action with no URL destination.
					 *
					 * COPY URL: uses the WAC design-library `<Button variant="primary">`
					 * via `<ShareButton />`.
					 */}
					<div css={bottomActionRowStyle}>
						{/*
						 * Restart is a real <button> styled to match Figma's "medium
						 * anchor default" spec (blue text, no border/background). See
						 * the twin comment in `estimate-step.tsx` for why we use a
						 * <button> here instead of the design-library `<Link>`: AFM's
						 * ratcheted `@atlassian/a11y/anchor-is-valid` rule cannot be
						 * disabled per-line, and the Restart action doesn't navigate,
						 * so a button is the correct semantic answer.
						 */}
						<button
							type="button"
							css={restartButtonStyle}
							onClick={() => {
								fireStep5Back();
								resetAll();
							}}
						>
							{intl.formatMessage(restartMessages.restart)}
						</button>
						<ShareButton
							label={
								METER_CONTENT[meter].summaryStep.shareCta
									? intl.formatMessage(METER_CONTENT[meter].summaryStep.shareCta)
									: undefined
							}
							onShare={fireStep5Share}
						/>
					</div>
					<EstimateDisclaimer />
				</>
			)}
		</>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Loading placeholder — shown below the header while CCP pricing data is
// pending. Two equal-width columns: the left stacks three 300px blocks, the
// right is a single 600px block. Each block mimics a summary card with a
// header bone and a run of text-line bones. Collapses to a single column
// below the md breakpoint.
// ─────────────────────────────────────────────────────────────────────────
const SkeletonBlock = ({ height }: { height: string }) => (
	<div css={skeletonBlockStyle} style={{ minHeight: height }}>
		<UniformSkeleton height="2.5rem" maxWidth="360px" />
		<UniformSkeleton count={4} height="1.25rem" lastLineWidth="60%" />
	</div>
);

const SummarySkeleton = () => (
	<div css={skeletonRowStyle} aria-busy="true" aria-live="polite">
		<div css={skeletonLeftColumnStyle}>
			<SkeletonBlock height="300px" />
			<SkeletonBlock height="300px" />
			<SkeletonBlock height="300px" />
		</div>
		<SkeletonBlock height="600px" />
	</div>
);

// ─────────────────────────────────────────────────────────────────────────
// Left-column: ResultsCards — content-driven, per-meter.
//
// Each meter's `summaryStep` object in the content registry may define
// any combination of three optional cards:
//   - orgNeedsCard       (all meters)
//   - whatsCoveredCard   (Rovo + Assets only)
//   - monthlyCostCard    (CSR only)
//
// The card copy comes entirely from the content registry, but the
// numeric values ({low}, {high}, {included}) come from calculator
// context selectors and are interpolated into the copy's placeholder
// tokens at render time. That way content designers can change the
// wording without touching component code, while dynamic numbers stay
// wired to state.
//
// See `meter-content.types.ts › SummaryStepContent` for the shape and
// the mapping decision matrix.
// ─────────────────────────────────────────────────────────────────────────
const SummaryResultsCards = () => {
	const intl = useIntl();
	// Formats an optional content descriptor, preserving `undefined` so the
	// ResultsCard slots stay unrendered when a meter omits the field.
	const format = (descriptor: MessageDescriptor | undefined, values?: Record<string, string>) =>
		descriptor ? intl.formatMessage(descriptor, values) : undefined;
	const meter = useSelector(selectMeter);
	const included = useSelector(selectIncludedAllowance);
	const { whatsCoveredCard, monthlyCostCard, overageCostCard } = METER_CONTENT[meter].summaryStep;
	// Locale/currency for number + price formatting. Both sourced from
	// the calculator's stored user context (defaults to en-US / USD).
	const locale = useSelector(getNumberFormatLocale);
	const currency = useSelector(getCurrencyIsoCode);
	// Real total monthly cost for the CSR "Monthly cost estimate" card,
	// per GWP-12277. Undefined while CCP pricing data is still loading —
	// we fall back to the static content-registry headline in that case
	// so the card renders something plausible during hydration.
	const totalCost = useSelector(selectTotalMonthlyCost);
	// GWP-12277: overage cost — shown only when the org's estimated usage
	// exceeds its included allowance. `hasOverage` gates the panel;
	// `overageCost` is the low/high USD range of the shortfall.
	const hasOverage = useSelector(selectHasOverage);

	const overageCost = useSelector(selectTotalOverageCost);
	// Overage units (extra meter units beyond the allowance) — drives the
	// Rovo "What's covered" card's gap copy ("There's a gap of X–Y credits").
	const overageUnits = useSelector(selectTotalOverageUnits);

	const includedText = formatNumber(included, locale);
	const monthlyCostText =
		totalCost !== undefined ? formatPrice(totalCost, { currency, locale }) : undefined;
	// Overage range headline, e.g. "USD $145–180": currency code prefix,
	// the low bound with its currency symbol, then the high bound as a
	// bare number to avoid repeating the symbol. Collapses to a single
	// value when the bounds are equal (e.g. a custom usage override).
	const overageRangeText = formatPriceRange(overageCost.low, overageCost.high, {
		currency,
		locale,
	});
	// For the Rovo "What's covered" overage copy: the gap size in meter
	// units ("1,200–1,800") and the estimated extra monthly charge
	// ("USD $145–180"), substituted into the card's `{overage}` / `{cost}`
	// tokens at render time.
	const overageUnitsText = formatNumberRange(overageUnits.low, overageUnits.high, locale);
	const overageChargeText = overageRangeText;
	// Per-unit rate line under the range. All display copy (unit noun, tier
	// phrasing) comes from the content registry's `rateNote`; here we only
	// substitute the `{rate}` / `{rate2}` … tokens with the currency-formatted
	// overage rate(s) sourced from the pricing config. Sub-dollar rates show
	// cents so "$0.50"/"$0.01" don't round to "$1"/"$0".
	const overageRateNote = overageCostCard
		? intl.formatMessage(
				overageCostCard.rateNote,
				buildOverageRateValues({ meter, currency, locale }),
			)
		: undefined;

	// Content-registry copy for the org-needs card usually contains a
	// [PLACEHOLDER] token for the usage range. We substitute the live
	// value here so what the customer sees is the actual number when
	// content has been finalized, but the placeholder still shows up
	// during content-team review.
	return (
		<>
			<OrgNeedsResultsCard testId="summary-org-needs" />
			{/*
			 * Monthly Cost Estimate card (GWP-12277 / Figma 2055:13212,
			 * 2164:28256): renders whenever a meter defines
			 * `overageCostCard`. Body copy switches between `body`
			 * (overage case) and `bodyCovered` (covered case) based on
			 * whether the org's estimated usage exceeds its included
			 * allowance. `listItems` are the meter's value props.
			 *
			 * Note: earlier logic gated this on `hasOverage` and fell
			 * through to `whatsCoveredCard` when covered. That meant the
			 * card only appeared in overage scenarios, which was wrong per
			 * the latest design — the card should always render (when
			 * defined) with body copy dynamic to the cost calculation.
			 */}
			{overageCostCard && (
				<ResultsCard
					eyebrow={intl.formatMessage(overageCostCard.eyebrow)}
					headline={overageRangeText}
					headlineSubtext={overageRateNote}
					coverageText={intl.formatMessage(
						hasOverage
							? overageCostCard.body
							: (overageCostCard.bodyCovered ?? overageCostCard.body),
					)}
					bullets={overageCostCard.listItems?.map((item) => intl.formatMessage(item))}
					testId="summary-overage-cost"
				/>
			)}
			{/*
			 * "What's covered" panel (Rovo Confluence spec Step 5, Card 4).
			 * Like the Monthly Cost Estimate card, this is DYNAMIC based on
			 * whether the org's estimated usage fits within its included
			 * allowance:
			 *   - headline        → live included allowance (e.g. "200,000")
			 *   - headlineSubtext → "<Meter> included monthly"
			 *   - covered case    → bodyCovered + listItemsCovered (value props)
			 *   - overage case    → bodyOverage + listItemsOverage (guidance)
			 * The `{overage}` / `{cost}` tokens in bodyOverage are replaced
			 * here with the live extra-usage range and estimated extra
			 * monthly charge. Falls back gracefully to the static headline
			 * when the two-state fields aren't defined for a meter.
			 */}
			{whatsCoveredCard && (
				<ResultsCard
					eyebrow={intl.formatMessage(whatsCoveredCard.eyebrow)}
					headline={includedText}
					headlineSubtext={format(whatsCoveredCard.headlineSubtext)}
					coverageText={
						hasOverage
							? format(whatsCoveredCard.bodyOverage, {
									overage: overageUnitsText,
									cost: overageChargeText,
								})
							: format(whatsCoveredCard.bodyCovered)
					}
					bullets={(hasOverage
						? whatsCoveredCard.listItemsOverage
						: whatsCoveredCard.listItemsCovered
					)?.map((item) => intl.formatMessage(item))}
					testId="summary-covered"
				/>
			)}
			{monthlyCostCard && (
				<ResultsCard
					eyebrow={intl.formatMessage(monthlyCostCard.eyebrow)}
					// `monthlyCostCard.headline` is optional; fall back to
					// empty string if neither a live cost nor a static
					// headline is available (ResultsCard's headline is
					// required).
					headline={monthlyCostText ?? format(monthlyCostCard.headline) ?? ''}
					headlineSubtext={format(monthlyCostCard.headlineSubtext)}
					footerNote={format(monthlyCostCard.footerNote)}
					testId="summary-monthly-cost"
				/>
			)}
		</>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// NOTE: The "How your teams might use {meter}" section is now a single
// source of truth — imported from `estimate-step.tsx` (exported
// `HowYourTeamsMightUse`). Both the Estimate and Summary steps render the
// exact same component + styling. The previous Summary-local duplicate
// (different Compiled-CSS styling + non-localized copy) was removed per
// GWP-12689 so the two screens stay visually and copy-wise in sync.
// ─────────────────────────────────────────────────────────────────────────
// Right-column: Selected product / collection cards.
//
// Renders one <ProductCardForKey /> per product the user selected on
// the Allowance step (from GWP-12299 / PR 408810). Cards are
// context-bound — they pull their own title/description/pricing from
// the calculator context.
//
// Multiple cards are separated by a horizontal rule (newer master
// treatment, PR ~410947+). If the user hasn't selected anything, we
// render a soft empty-state so the column still has structure — rare,
// since the Allowance step normally blocks Next until at least one
// product is selected.
// ─────────────────────────────────────────────────────────────────────────
const SelectedProductCards = () => {
	const intl = useIntl();
	const selectedProducts = useSelector(selectSelectedProducts);
	if (selectedProducts.length === 0) {
		return (
			<div css={placeholderCardStyle}>
				<p css={placeholderLabelStyle}>{intl.formatMessage(messages.noProductsSelected)}</p>
				<p css={placeholderNoteStyle}>{intl.formatMessage(messages.noProductsSelectedNote)}</p>
			</div>
		);
	}
	return (
		<div css={selectedProductCardsStyle}>
			{selectedProducts.map((product, index: number) => (
				<div key={product.productKey}>
					{index > 0 && <hr role="presentation" css={hrProductCard} />}
					<ProductCardForKey productKey={product.productKey} />
				</div>
			))}
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────

// Header per Figma node 1103:32406 — 80px above the H3 (40px base + 40
// added per design refinement) and 40px below the subtitle.
const pageHeaderStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	paddingBlockStart: token('space.1000'),
	paddingBlockEnd: token('space.500'),
});

const pageTitleStyle = css({
	// TypographyStyles maps raw `h3` to Charlie Display Bold 32/38.
	margin: 0,
});

const pageSubtitleStyle = css({
	// Body Large: Charlie Text Regular 20/28.
	margin: 0,
	fontSize: '1.25rem',
	lineHeight: 1.4,
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
});

// Placeholder card (empty-state when no products selected on Allowance).
const placeholderCardStyle = css({
	paddingBlock: token('space.500'),
	paddingInline: token('space.300'),
	borderRadius: wacToken('corner.radius.card'),
	border: `2px dashed ${wacToken('color.neutral.light400')}`,
	backgroundColor: wacToken('color.neutral.light100'),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.100'),
	minHeight: '320px',
	textAlign: 'center',
});

const placeholderLabelStyle = css({
	margin: 0,
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '24px',
	lineHeight: '30px',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
});

const placeholderNoteStyle = css({
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '20px',
	color: wacToken('color.neutral.light800'),
});

// Skeleton loading layout — two equal-width columns on desktop, single
// column below the md breakpoint. The left cell stacks three blocks; the
// right cell holds one tall block.
const skeletonRowStyle = css({
	display: 'grid',
	gridTemplateColumns: '1fr',
	gap: token('space.400'),
	alignItems: 'start',
	[media.above.md]: {
		gridTemplateColumns: '1fr 1fr',
	},
});

const skeletonLeftColumnStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

// A single skeleton block — mirrors a summary card: a header bone on top and
// a run of text-line bones below, laid over a card-like surface. The block's
// height is driven by the caller via an inline `minHeight`.
const skeletonBlockStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	paddingBlock: token('space.300'),
	paddingInline: token('space.300'),
	borderRadius: wacToken('corner.radius.card'),
	backgroundColor: wacToken('color.neutral.light100'),
});

// Two-column layout — collapses to single column below ~832px via
// `repeat(auto-fit, minmax(400px, 1fr))`. Matches Estimate step.
const twoColumnRowStyle = css({
	display: 'grid',
	// See Estimate step: use `min(400px, 100%)` so a lone card on a
	// narrow viewport (< 400px) doesn't force horizontal overflow.
	gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
	gap: token('space.400'),
	alignItems: 'start',
});

const leftColumnStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const rightColumnStyle = css({
	display: 'flex',
	flexDirection: 'column',
});

// NOTE: The value-cards panel + value-prop styles that used to live here
// were removed alongside the local `HowYourTeamsMightUse` duplicate — the
// shared component imported from `estimate-step.tsx` owns its own styling
// now (single source of truth, GWP-12689).

// Selected product cards — stacked vertically with a gap.
const selectedProductCardsStyle = css({
	display: 'flex',
	flexDirection: 'column',
	borderRadius: wacToken('corner.radius.card'),
	background: wacToken('color.neutral.light100'),
});

const hrProductCard = css({
	border: 0,
	background: wacToken('color.neutral.light400'),
	height: '1px',
	width: 'auto',
	marginBlock: token('space.100'),
	marginInline: token('space.200'),
});

// Feedback wrapper — 56px above, 44px below (matches Estimate step).
// Horizontally centered so the prompt + thumbs sit in the middle of
// the calculator container, on all viewports. FeedbackButtons handles
// row-vs-column switching + internal centering per breakpoint.
const feedbackWrapperStyle = css({
	display: 'flex',
	// Mobile (< 768px): left-align so prompt + thumbs sit at the same
	// x-origin as the surrounding content grid.
	// Desktop (>= 768px): centered per Figma.
	justifyContent: 'flex-start',
	paddingBlockStart: '56px',
	paddingBlockEnd: '44px',
	[media.above.md]: {
		justifyContent: 'center',
	},
});

// Restart button — matches Figma "medium anchor default": blue text,
// no border, no background, Charlie Text Bold 16/24 with underline on
// hover/focus for affordance. Focus-visible ring for keyboard users.
// KEEP IN SYNC with the identical `restartButtonStyle` in
// `estimate-step.tsx` (GWP-12674). Compiled CSS's `@compiled/no-exported-css`
// rule forbids exporting `css()` return values because the transformer
// needs to inline the style at build time in the file where it's used —
// so we duplicate rather than centralize. If you edit either copy, edit
// the other too.
const restartButtonStyle = css({
	appearance: 'none',
	background: 'transparent',
	border: 'none',
	padding: 0,
	margin: 0,
	color: wacToken('color.primary.blue'),
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	fontWeight: 400,
	lineHeight: '24px',
	cursor: 'pointer',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'underline',
	},
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
		borderRadius: '2px',
	},
});

// Bottom action row — Restart button + Share button, side-by-side,
// vertically centered. Mirrors Estimate step's `bottomActionRowStyle`.
const bottomActionRowStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.150'),
});
